// src/App.tsx
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";
import {
  Layout,
  Menu,
  ConfigProvider,
  theme,
  Typography,
  Button,
  Drawer,
} from "antd";
import {
  SearchOutlined,
  CodeOutlined,
  ThunderboltOutlined,
  AppstoreOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  FileSearchOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import "./App.css";

// Import Pages
import KeywordTool from "./pages/KeywordTool";
import AuditTool from "./pages/AuditTool";
import MetaGen from "./pages/MetaGen";
import RobotsGen from "./pages/RobotsGen";
import ContentAnalyzer from "./pages/ContentAnalyzer";

const { Header, Content, Sider } = Layout;

const AppLayout: React.FC = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle screen resize to auto-collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setCollapsed(true);
      else setCollapsed(false);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Init
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const items = [
    {
      key: "/",
      icon: <SearchOutlined />,
      label: <Link to="/">Keyword Intelligence</Link>,
    },
    {
      key: "/audit",
      icon: <ThunderboltOutlined />,
      label: <Link to="/audit">Performance Audit</Link>,
    },
    {
      key: "/content",
      icon: <FileSearchOutlined />,
      label: <Link to="/content">Content Analyzer</Link>,
    },
    {
      key: "/meta",
      icon: <CodeOutlined />,
      label: <Link to="/meta">Meta Tag Builder</Link>,
    },
    {
      key: "/robots",
      icon: <FileTextOutlined />,
      label: <Link to="/robots">Robots.txt Gen</Link>,
    },
  ];

  const MenuContent = (
    <>
      <div
        style={{
          height: 64,
          margin: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
        }}
      >
        <AppstoreOutlined style={{ fontSize: 24, color: "#1890ff" }} />
        {!collapsed && (
          <Typography.Title
            level={4}
            style={{
              margin: 0,
              color: "#fff",
              letterSpacing: "1px",
              whiteSpace: "nowrap",
            }}
          >
            SEO<span style={{ color: "#1890ff" }}>STUDIO</span>
          </Typography.Title>
        )}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={items}
        style={{ background: "transparent", fontSize: "15px", border: "none" }}
        onClick={() => isMobile && setCollapsed(true)} // Close on click (mobile)
      />
    </>
  );

  return (
    <Layout style={{ minHeight: "100vh", background: "transparent" }}>
      {/* Desktop Sider - Hidden on Mobile */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={260}
          style={{
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(10px)",
            borderRight: "1px solid rgba(255,255,255,0.05)",
            position: "sticky",
            top: 0,
            height: "100vh",
            zIndex: 10,
          }}
        >
          {MenuContent}
        </Sider>
      )}

      {/* Mobile Drawer - Replaces Sider on small screens */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setCollapsed(true)}
          open={!collapsed}
          bodyStyle={{ padding: 0, background: "#0f172a" }}
          width={260}
          closable={false}
        >
          {MenuContent}
        </Drawer>
      )}

      <Layout style={{ background: "transparent" }}>
        <Header
          style={{
            padding: "0 16px",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: "16px",
              width: 45,
              height: 45,
              color: "#fff",
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          />
          <Typography.Title
            level={4} // Smaller title for better mobile fit
            style={{
              margin: 0,
              color: "#fff",
              fontWeight: 300,
              fontSize: isMobile ? "1.1rem" : "1.5rem",
            }}
            ellipsis
          >
            {location.pathname === "/" && "Keyword Intelligence"}
            {location.pathname === "/audit" && "Site Audit"}
            {location.pathname === "/meta" && "Meta Builder"}
            {location.pathname === "/content" && "Content Analyzer"}
            {location.pathname === "/robots" && "Robots.txt Generator"}
          </Typography.Title>
        </Header>

        <Content
          style={{
            margin: isMobile ? "16px 12px 0" : "24px 24px 0",
            overflow: "initial",
            minHeight: 280,
          }}
        >
          <Routes>
            <Route path="/" element={<KeywordTool />} />
            <Route path="/audit" element={<AuditTool />} />
            <Route path="/meta" element={<MetaGen />} />
            <Route path="/robots" element={<RobotsGen />} />
            <Route path="/content" element={<ContentAnalyzer />} />
          </Routes>
          <div
            style={{
              textAlign: "center",
              padding: "30px 10px",
              color: "#666",
              fontSize: "0.8rem",
            }}
          >
            SEOSTUDIO ©{new Date().getFullYear()} — Built for Creators
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

const App: React.FC = () => (
  <ConfigProvider
    theme={{
      algorithm: theme.darkAlgorithm,
      token: {
        colorPrimary: "#3b82f6",
        borderRadius: 12,
        fontFamily: "'Inter', sans-serif",
        colorBgContainer: "#1e293b",
      },
      components: {
        Card: {
          headerFontSize: 18,
          fontWeightStrong: 600,
        },
        Button: {
          fontWeight: 600,
        },
      },
    }}
  >
    <Router>
      <AppLayout />
    </Router>
  </ConfigProvider>
);

export default App;
